import { useEffect, useState } from "react";
import Header from "@web-ui/components/Header";
import type { NavigationItem } from "@web-ui/types/navigation";
import { ConsoleSidebar } from "./sidebar";

/**
 * The calm frame (section 2): the kit's Layout (web-ui/components/Layout) with the console's own
 * sidebar. The header carries the page title and its line, the changes pill and the signed-in
 * person; a page's breadcrumbs and its one primary action sit at the top of the page (PageBar).
 * Neutral greys throughout; the brand's accent only on primary actions, focus and selection.
 */
export function ConsoleFrame({ title, subtitle, mark, top, navigation, actions, user, children }: {
  title: string;
  subtitle?: string;
  /** The brand's mark for the phone header (the sidebar is a drawer there). */
  mark: React.ReactNode;
  /** The sidebar's top: the brand switcher. */
  top: React.ReactNode;
  navigation: NavigationItem[];
  actions?: React.ReactNode;
  user: React.ComponentProps<typeof Header>["user"];
  children: React.ReactNode;
}) {
  const [drawer, setDrawer] = useState(false);

  useEffect(() => {
    const width = window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.style.setProperty("--scrollbar-width", `${width}px`);
    const close = () => setDrawer(false);
    window.addEventListener("hashchange", close);
    return () => { document.documentElement.style.removeProperty("--scrollbar-width"); window.removeEventListener("hashchange", close); };
  }, []);

  return (
    <div className="ps-85 max-4xl:ps-70 max-3xl:ps-60 max-xl:ps-0">
      <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:start-3 focus:z-50 focus:rounded-full focus:bg-b-surface2 focus:px-4 focus:py-2 focus:text-button">Skip to content</a>
      <ConsoleSidebar top={top} navigation={navigation} open={drawer} onClose={() => setDrawer(false)} />
      <div
        className={`fixed inset-0 z-30 bg-shade-07/70 transition-[opacity,visibility] duration-(--motion-slow) ease-(--ease-out-quint) dark:bg-shade-04/90 xl:hidden ${drawer ? "visible opacity-100" : "invisible opacity-0"}`}
        onClick={() => setDrawer(false)}
        aria-hidden="true"
      />
      <Header title={title} subtitle={subtitle} brand={mark} actions={actions} user={user} onToggleSidebar={() => setDrawer(!drawer)} toggleSidebarLabel="Open menu" />
      <div className={`pb-5 max-md:pt-18 ${subtitle ? "pt-26" : "pt-22"}`}>
        <div className="center-with-sidebar">
          <main id="main" tabIndex={-1} className="flex flex-col gap-3 outline-none [&_.card]:mb-0">
            {subtitle ? <p className="text-body-2 text-t-secondary md:hidden">{subtitle}</p> : null}
            {children}
          </main>
        </div>
      </div>
    </div>
  );
}
